<template>
    <div class="w-full max-w-30rem mb-6">
        <div class="text-center w-full mb-6">
            <span class="H6-Medium block text-center">You're upgrading to {{ targetPackage?.name }}</span>
            <span class="Label block text-center mt-1 mb-6">Confirm your upgrade to unlock {{ targetPackage?.name }} features</span>
        </div>

        <span class="Label mb-3 block">What's included</span>
        <ul class="list-none p-0 m-0 mb-5 text-sm text-700 flex flex-column gap-2">
            <li><i class="pi pi-check Label mr-2"></i> Maximum of {{ targetPackage?.features.maxTutors }} Tutors</li>
            <li><i class="pi pi-check Label mr-2"></i> Maximum of {{ targetPackage?.features.maxStudents }} Students</li>
            <li v-if="targetPackage?.features.securePayments"><i class="pi pi-check Label mr-2"></i> Secure payments</li>
            <li v-if="targetPackage?.features.aiFeatures"><i class="pi pi-check Label mr-2"></i> AI features</li>
            <li v-if="targetPackage?.features.pastPapers"><i class="pi pi-check Label mr-2"></i> Past papers</li>
            <li v-if="targetPackage?.features.inAppChat"><i class="pi pi-check Label mr-2"></i> In app chat</li>
        </ul>

        <hr class="border-top-1 border-none border-gray-200 mb-4" style="width: 120%; margin-left: -10%;" />

        <!-- Price row -->
        <div class="w-full flex justify-content-between align-items-center mb-3 text-sm">
            <span class="Label">Amount charged today</span>
            <div class="flex align-items-center gap-2">
                <span v-if="appliedDiscount" class="Label text-400 line-through text-xs">Rs. {{ targetPackage?.priceLabel }}</span>
                <span class="Label font-semibold" :class="{ 'text-green-600': appliedDiscount }">
                    <template v-if="appliedDiscount?.isFree">FREE</template>
                    <template v-else>Rs. {{ appliedDiscount ? appliedDiscount.discountedAmount : targetPackage?.priceLabel }}</template>
                </span>
            </div>
        </div>

        <!-- Promo code section -->
        <div class="mb-4">
            <!-- Input state -->
            <div v-if="!appliedDiscount" class="promo-field">
                <label class="promo-label">
                    PROMO CODE <span class="promo-optional">(Optional)</span>
                </label>
                <div class="promo-input-row">
                    <InputText
                        v-model="promoCode"
                        placeholder="Enter promo code"
                        class="promo-input"
                        :disabled="isApplying"
                        @keyup.enter="applyPromoCode"
                        @input="promoError = null; showPromoWarning = false"
                    />
                    <Button
                        label="Apply"
                        class="promo-apply-btn"
                        :loading="isApplying"
                        :disabled="isApplying || !promoCode.trim()"
                        @click="applyPromoCode"
                    />
                </div>
                <!-- Fixed-height message area — no layout shift -->
                <div class="promo-msg-area">
                    <small v-if="promoError" class="promo-error-msg">{{ promoError }}</small>
                    <div v-else-if="showPromoWarning" class="promo-warning-msg">
                        <i class="pi pi-exclamation-triangle promo-warn-icon"></i>
                        <span>Apply your code first, or
                            <span class="promo-action-link" @click="removeDiscount">remove it</span>
                            /
                            <span class="promo-action-link" @click="continueWithoutCode">continue without code</span>.
                        </span>
                    </div>
                </div>
            </div>

            <!-- Applied state — same vertical space -->
            <div v-else class="promo-applied-banner">
                <div class="flex flex-column gap-1 flex-1">
                    <div class="flex align-items-center gap-2">
                        <i class="pi pi-check-circle text-green-600"></i>
                        <span class="text-xs font-semibold text-green-700">
                            {{ appliedDiscount.isFree ? 'Code applied — This month is FREE!' : 'Promo code applied!' }}
                        </span>
                    </div>
                    <span v-if="!appliedDiscount.isFree" class="text-xs text-green-600 ml-4">You saved LKR {{ appliedDiscount.discountSavedAmount }}</span>
                    <div v-if="appliedDiscount.isFree && !hasToken" class="free-notice">
                        <i class="pi pi-exclamation-triangle free-notice-icon"></i>
                        <span>A refundable LKR 30 card verification is required to save your card for future monthly renewals.</span>
                    </div>
                </div>
                <span class="text-xs text-400 cursor-pointer underline ml-3 flex-shrink-0" @click="removeDiscount">Remove</span>
            </div>
        </div>

        <hr class="w-full border-top-1 border-none border-gray-200 mb-4" />

        <div class="w-full flex justify-content-between mb-3 text-sm">
            <span class="Label">Next billing date</span>
            <span class="Label font-semibold">{{ nextBillingDate }}</span>
        </div>
        <div class="w-full flex justify-content-between text-sm">
            <span class="Label">Billing cycle</span>
            <span class="Label font-semibold">Monthly</span>
        </div>

        <div class="w-full flex flex-column p-6">
            <Button :label="upgradeButtonLabel" class="w-full custom-brown-btn mb-3" :disabled="loading" :loading="loading" @click="handleConfirm" />
            <Button label="Keep current plan" class="w-full p-button-outlined p-button-secondary" :disabled="loading" @click="$emit('cancel')" />
        </div>

        <p class="Label text-xs text-center">Your subscription will auto-renew. Cancel anytime from your billing settings.</p>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { backendApi } from '@/api/axios';
import { DISCOUNT_ENDPOINTS } from '@/api/endpoints';

const props = defineProps({
    targetPackage:   Object,
    nextBillingDate: String,
    loading:         { type: Boolean, default: false },
    hasToken:        { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'cancel']);

const upgradeButtonLabel = computed(() => {
    if (!appliedDiscount.value) return `Upgrade to ${props.targetPackage?.name || 'Premium'}`;
    if (appliedDiscount.value.isFree) {
        return props.hasToken ? 'Activate Free Month' : 'Verify Card & Upgrade';
    }
    return `Upgrade to ${props.targetPackage?.name || 'Premium'} — LKR ${appliedDiscount.value.discountedAmount}`;
});

// Promo state
const promoCode        = ref('');
const isApplying       = ref(false);
const appliedDiscount  = ref(null);
const promoError       = ref(null);
const showPromoWarning = ref(false);

function resetPromo() {
    promoCode.value        = '';
    isApplying.value       = false;
    appliedDiscount.value  = null;
    promoError.value       = null;
    showPromoWarning.value = false;
}

async function applyPromoCode() {
    if (!promoCode.value.trim()) return;
    promoError.value       = null;
    showPromoWarning.value = false;
    isApplying.value       = true;
    try {
        const subscriptionPrice = parseFloat(props.targetPackage?.priceLabel) || 0;
        const res  = await backendApi.post(DISCOUNT_ENDPOINTS.APPLY, {
            code: promoCode.value.trim(),
            subscriptionPrice,
        });
        const data = res.data?.data ?? res.data;
        appliedDiscount.value = {
            discountId:          data.discountId,
            discountedAmount:    parseFloat(data.amountToPay),
            discountSavedAmount: parseFloat(data.savedAmount),
            isFree:              data.amountToPay === '0.00',
        };
    } catch (err) {
        promoError.value = err?.response?.data?.message || 'Invalid promo code. Please try again.';
    } finally {
        isApplying.value = false;
    }
}

function removeDiscount() {
    resetPromo();
}

function handleConfirm() {
    if (promoCode.value.trim() && !appliedDiscount.value) {
        promoError.value       = null;
        showPromoWarning.value = true;
        return;
    }
    doConfirm();
}

function doConfirm() {
    showPromoWarning.value = false;
    emit('confirm', { discount: appliedDiscount.value });
}

function continueWithoutCode() {
    promoCode.value        = '';
    promoError.value       = null;
    showPromoWarning.value = false;
    doConfirm();
}
</script>

<style scoped>
.custom-brown-btn {
    background-color: var(--yellow-500) !important;
    border: 1px solid var(--yellow-500) !important;
    color: white !important;
    font-weight: 600;
}
.custom-brown-btn:hover {
    background-color: var(--yellow-600) !important;
    border-color: var(--yellow-600) !important;
}
.p-button-outlined {
    background-color: var(--accent-50) !important;
    font-weight: 600;
    color: black !important;
}
.p-button-outlined:hover {
    background-color: var(--accent-100) !important;
}

/* Promo */
.promo-field {
    display: flex;
    flex-direction: column;
}
.promo-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: #6B7280;
    text-transform: uppercase;
    display: block;
    margin-bottom: 5px;
}
.promo-optional {
    font-size: 10px;
    font-weight: 400;
    color: #9CA3AF;
    text-transform: none;
    letter-spacing: 0;
}
.promo-input-row {
    display: flex;
    gap: 8px;
    align-items: center;
}
.promo-input {
    flex: 1;
    font-size: 0.875rem;
    height: 36px;
}
.promo-apply-btn {
    background-color: var(--yellow-500) !important;
    border: 1px solid var(--yellow-500) !important;
    color: #fff !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    height: 36px;
    padding: 0 1rem !important;
    border-radius: 6px !important;
    white-space: nowrap;
}
.promo-apply-btn:hover:not(:disabled) {
    background-color: var(--yellow-600) !important;
    border-color: var(--yellow-600) !important;
}
.promo-msg-area {
    min-height: 36px;
    padding-top: 4px;
    display: flex;
    align-items: flex-start;
}
.promo-error-msg {
    font-size: 11px;
    color: #EF4444;
}
.promo-warning-msg {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-size: 11px;
    color: #92400E;
    background: #FFFBEB;
    border: 1px solid #FDE68A;
    border-radius: 6px;
    padding: 5px 8px;
    line-height: 1.5;
    width: 100%;
}
.promo-warn-icon {
    font-size: 10px;
    margin-top: 2px;
    flex-shrink: 0;
    color: #D97706;
}
.promo-action-link {
    color: #b45309;
    cursor: pointer;
    text-decoration: underline;
    white-space: nowrap;
}
.promo-action-link:hover {
    color: #92400e;
}
.promo-applied-banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 10px 12px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
}

.free-notice {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-size: 11px;
    color: #92400E;
    background: #FFFBEB;
    border: 1px solid #FDE68A;
    border-radius: 6px;
    padding: 5px 8px;
    line-height: 1.5;
    margin-left: 20px;
    margin-top: 4px;
}

.free-notice-icon {
    font-size: 10px;
    margin-top: 2px;
    flex-shrink: 0;
    color: #D97706;
}
</style>
